import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Users, ExternalLink, ArrowLeft, Search } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import BoxSeatLogo from '../components/BoxSeatLogo';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const amberIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export default function VenuesPage() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append('venue_type', typeFilter);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/venues?${params.toString()}`);
      setVenues(res.data);
    } catch (err) {
      console.error('Failed to fetch venues:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVenues();
  };

  const venueTypes = ['stadium', 'arena', 'bar', 'restaurant', 'park'];
  const center = useMemo(() => [39.9012, -75.1720], []); // South Philly Sports Complex
  const venuesWithCoords = useMemo(() => venues.filter(v => v.latitude && v.longitude), [venues]);

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-[1000] backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-zinc-400 hover:text-white transition-colors"><ArrowLeft size={20} /></button>
            <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/events')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase hidden md:block">Events</button>
            <button onClick={() => navigate('/meetups')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase hidden md:block">Meetups</button>
            <button onClick={() => navigate('/login')} className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber">Sign In</button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Explore</p>
          <h1 data-testid="venues-page-title" className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase">
            Philadelphia <span className="text-primary">Venues</span>
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                data-testid="venues-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search venues..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-primary text-black font-bold uppercase text-sm rounded-md hover:bg-primary-hover transition-all">Search</button>
          </form>
          <select
            data-testid="venues-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="">All Types</option>
            {venueTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>

        {/* Map */}
        <div className="rounded-xl overflow-hidden border border-white/10 mb-8" style={{ height: '400px' }}>
          <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            {venuesWithCoords.map((v) => (
              <Marker key={v.id} position={[v.latitude, v.longitude]} icon={amberIcon}>
                <Popup>
                  <div className="text-sm">
                    <strong className="text-base">{v.name}</strong>
                    <p className="text-gray-400 mt-1">{v.address}</p>
                    <p className="text-gray-400">{v.venue_type}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Venues Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : venues.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((v) => (
              <div key={v.id} data-testid="venue-card" className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                {v.image_url && <img src={v.image_url} alt={v.name} className="w-full h-44 object-cover" />}
                <div className="p-6">
                  <span className="text-xs font-bold tracking-[0.15em] uppercase text-primary">{v.venue_type}</span>
                  <h3 className="font-heading text-xl font-bold uppercase mt-1 mb-2">{v.name}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{v.description}</p>
                  <div className="space-y-2 text-sm text-zinc-500">
                    <div className="flex items-center gap-2"><MapPin size={14} /> {v.address}, {v.city}, {v.state}</div>
                    {v.capacity && <div className="flex items-center gap-2"><Users size={14} /> Capacity: {v.capacity.toLocaleString()}</div>}
                  </div>
                  {v.amenities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-4">
                      {v.amenities.map((a) => <span key={a} className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded-full">{a}</span>)}
                    </div>
                  )}
                  {v.website && (
                    <a href={v.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-primary mt-4 hover:underline">
                      <ExternalLink size={14} /> Visit Website
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <MapPin size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="font-heading text-2xl font-bold uppercase mb-2">No Venues Found</h3>
            <p className="text-zinc-500">Try adjusting your search or filters.</p>
          </div>
        )}
      </main>
    </div>
  );
}
